import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import {
  pageSectionSubtitleSx,
  pageSectionTitleSx,
} from "../../../constants/pageSectionStyles";
import { brandContainedButtonSx } from "../../../constants/brandContainedButtonStyles";
import { useBrandThemeColors } from "../../../branding";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useHasPrivilegeStatus } from "../../../hooks/useHasPrivilege";
import { useAcTenantId } from "../../../hooks/useAcTenantId";
import { useAssessmentNameOptions } from "../../../hooks/useAssessmentNameOptions";
import { useAutoTaggingPlanTypes } from "../../../hooks/useAutoTaggingPlanTypes";
import {
  buildAutoTaggingPlanSubmitItem,
  fetchAutoTaggingPlanById,
  importAutoTaggingPlansFromFile,
  submitAssessmentPlanItems,
  updateAutoTaggingPlan,
} from "../../../services/autoTaggingService";
import { AUTO_TAGGING_PRIVILEGES } from "../../../services/permissionsService";
import type { ConceptValue } from "../../../services/regulationsService";
import { useRegulationsNextShellIntegration } from "../../shared/useRegulationsNextShellIntegration";
import arrowLeftIcon from "../../../assets/icon-left.svg";
import DiscardEnhancedTaggingRuleChangesDialog from "../EnhancedTaggingRule/DiscardEnhancedTaggingRuleChangesDialog";
import {
  ImpactedItemsCascadePicker,
  ReferenceItemsCascadePicker,
  type ImpactedCascadeSelection,
  type ReferenceDimensionSelection,
} from "./ClassificationCascadePicker";
import AssessmentNameField from "./AssessmentNameField";
import AssessmentPromptFieldWithDefault from "./AssessmentPromptFieldWithDefault";
import { ASSESSMENT_PROMPT_DEFAULT_INFO } from "./AssessmentPromptField";
import ClassificationPlanConfigFields, {
  ClassificationPlanFlowArrow,
} from "./ClassificationPlanConfigFields";
import {
  buildAssessmentRecordItems,
  buildReferenceItemsFromObjectSubType,
  mapAssessmentTemplateToApi,
} from "./classificationPlanApiMapping";
import {
  CLASSIFICATION_PROMPT_MAX_LENGTH,
  createEmptyClassificationPlanConfig,
  parseClassificationLayer,
  shouldShowPrompt,
  shouldShowTaggingApproach,
} from "./classificationPlanConfigOptions";
import DefaultAssessmentPromptPrefetch from "./DefaultAssessmentPromptPrefetch";
import { isEditFormDirty } from "./editAssessmentPlanForm";
import ExtractFromFileDialog from "./ExtractFromFileDialog";
import { mapTaggingPlanRow } from "./mapTaggingPlanRow";
import {
  areAllComboStatsLoaded,
  assertEditComboAvailability,
  buildPlanLimitToastMessage,
  collectExceededConceptLimits,
  collectPlanLimitClearItemIds,
  isConceptComboSelectionPatch,
  isConceptPlanLimitError,
  validateImportedFormItems,
  type ConceptComboDraftItem,
  type ConceptPlanLimitItem,
} from "./conceptStatsValidation";
import { useDraftConceptStats } from "./useDraftConceptStats";
import { AI_PLAN_PATH, type TaggingPlanRow } from "../constants";
import {
  hasDraftItemFieldErrors,
  PLAN_LIMIT_CONCEPT_ERROR,
  validateDraftItem,
  type DraftItemFieldErrors,
  type DraftItemFieldKey,
} from "./validateDraftItem";

const sectionLabelSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#262626",
  mb: 1,
};

type DraftItem = {
  id: string;
  objectType: string;
  objectSubType: string;
  /** Display labels for Reference Dimension values (comma-separated). */
  objectSubTypeName?: string;
  /** Reference Dimension concept (`kind=reference`). */
  referenceConceptCode?: string;
  referenceConceptDisplayName?: string;
  /** Multi-Dimension Reference selections (`reference_items`). */
  referenceDimensions?: ReferenceDimensionSelection[];
  conceptCode: string;
  conceptValues: ConceptValue[];
  assessmentPrompt: string;
  allValues?: boolean;
  taggingApproach: string;
  execution: string;
  /** API `layer` as digit string ("" = unset). */
  layer: string;
  assessmentTemplate: string;
  updateAssessmentRecordConcept: string;
  updateAssessmentRecords: string[];
};

/**
 * Stable unique draft ids — do not use a module counter.
 * HMR can reset module state while React keeps prior draftItems, which reused
 * sequential ids and made Add One Item rows share updates.
 */
function createDraftItemId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `draft-item-${crypto.randomUUID()}`;
  }
  return `draft-item-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function createEmptyDraftItem(defaults?: {
  objectType: string;
  objectSubType: string;
}): DraftItem {
  return {
    id: createDraftItemId(),
    objectType: defaults?.objectType ?? "",
    objectSubType: defaults?.objectSubType ?? "",
    conceptCode: "",
    conceptValues: [],
    assessmentPrompt: "",
    allValues: false,
    // Fresh config per draft — do not share array refs across Add One Item rows.
    ...createEmptyClassificationPlanConfig(),
  };
}

function createDraftItemFromImport(
  imported: Awaited<ReturnType<typeof importAutoTaggingPlansFromFile>>[number],
): DraftItem {
  return {
    id: createDraftItemId(),
    objectType: imported.objectType,
    objectSubType: imported.objectSubType,
    conceptCode: imported.conceptCode,
    conceptValues: [...imported.conceptValues],
    assessmentPrompt: imported.assessmentPrompt,
    allValues: imported.allValues,
    ...createEmptyClassificationPlanConfig(),
  };
}

function applyImportedNames(
  items: Awaited<ReturnType<typeof importAutoTaggingPlansFromFile>>,
  drafts: DraftItem[],
): Record<string, string> {
  const names: Record<string, string> = {};
  drafts.forEach((draft, index) => {
    const imported = items[index];
    if (!imported) return;
    names[draft.id] = imported.assessmentName?.trim() || "";
  });
  return names;
}

function mapRowConceptValues(
  values: TaggingPlanRow["conceptValues"],
): ConceptValue[] {
  return values.map((value) => ({
    id: value.code,
    code: value.code,
    name: value.name.trim() || value.code,
    description: value.description,
  }));
}

function createDraftItemFromPlan(plan: TaggingPlanRow): DraftItem {
  return {
    id: plan.id,
    objectType: plan.objectType,
    objectSubType: plan.objectSubType,
    objectSubTypeName: plan.objectSubTypeName || undefined,
    referenceConceptCode: plan.referenceConceptCode || undefined,
    referenceConceptDisplayName:
      plan.referenceConceptDisplayName || undefined,
    referenceDimensions: plan.referenceDimensions.map((dim) => ({
      conceptCode: dim.conceptCode,
      conceptDisplayName: dim.conceptDisplayName,
      allValues: dim.allValues,
      values: dim.values.map((value) => ({
        id: value.code,
        code: value.code,
        name: value.name,
        description: value.description ?? "",
      })),
    })),
    conceptCode: plan.conceptCode,
    conceptValues: mapRowConceptValues(plan.conceptValues),
    assessmentPrompt: plan.description,
    allValues: plan.allValues,
    taggingApproach: plan.taggingApproach,
    execution: plan.execution,
    layer:
      Number.isFinite(plan.layer) && plan.layer >= 0
        ? String(Math.floor(plan.layer))
        : "",
    assessmentTemplate: plan.assessmentTemplate,
    updateAssessmentRecordConcept: plan.updateAssessmentRecordConcept,
    updateAssessmentRecords: [...plan.updateAssessmentRecords],
  };
}

function buildClassificationSubmitFields(item: {
  objectSubType: string;
  objectSubTypeName?: string;
  referenceConceptCode?: string;
  referenceDimensions?: ReferenceDimensionSelection[];
  layer: string;
  assessmentTemplate: string;
  updateAssessmentRecordConcept: string;
  updateAssessmentRecords: string[];
}) {
  const layer = parseClassificationLayer(item.layer);
  // Tagging Approach is hidden — do not include tagging_approach on write.
  return {
    layer: layer ?? 0,
    referenceItems: buildReferenceItemsFromObjectSubType({
      objectSubType: item.objectSubType,
      objectSubTypeName: item.objectSubTypeName,
      referenceConceptCode: item.referenceConceptCode,
      referenceDimensions: item.referenceDimensions,
    }),
    assessmentRecordItems: buildAssessmentRecordItems(
      item.updateAssessmentRecords,
      item.updateAssessmentRecordConcept,
    ),
    assessmentType: mapAssessmentTemplateToApi(item.assessmentTemplate),
  };
}

export default function AddAssessmentPlanItemsPage() {
  const acTenantId = useAcTenantId();
  return (
    <AddAssessmentPlanItemsPageContent key={acTenantId || "no-ac-tenant"} />
  );
}

function AddAssessmentPlanItemsPageContent() {
  useRegulationsNextShellIntegration();
  const navigate = useNavigate();
  const location = useLocation();
  const { planId: planIdParam } = useParams<{ planId?: string }>();
  const isEditMode = Boolean(planIdParam?.trim());
  const planId = planIdParam?.trim() || "";
  const { primaryColor, primaryFontColor } = useBrandThemeColors();
  const { showMessage } = useSnackbar();
  const { has: canManageAutoTagging, loaded: privilegesLoaded } =
    useHasPrivilegeStatus(AUTO_TAGGING_PRIVILEGES.manage);
  const {
    defaultObjectType,
    getSubTypesForObjectType,
  } = useAutoTaggingPlanTypes({ configurableOnly: true });
  /** Assessment Name options load on dropdown open only (not on page enter). */
  const [assessmentNameMenuActive, setAssessmentNameMenuActive] = useState(false);
  const [assessmentNameReloadToken, setAssessmentNameReloadToken] = useState(0);
  const { options: assessmentNameOptions, loading: assessmentNamesLoading } =
    useAssessmentNameOptions({
      enabled: assessmentNameMenuActive,
      reloadToken: assessmentNameReloadToken,
    });

  const [draftItems, setDraftItems] = useState<DraftItem[]>(() =>
    isEditMode ? [] : [createEmptyDraftItem()],
  );
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [importing, setImporting] = useState(false);
  const [extractDialogOpen, setExtractDialogOpen] = useState(false);
  const [fieldErrorsByItemId, setFieldErrorsByItemId] = useState<
    Record<string, DraftItemFieldErrors>
  >({});
  const [impactedByItemId, setImpactedByItemId] = useState<
    Record<string, ImpactedCascadeSelection>
  >({});
  const [nameByItemId, setNameByItemId] = useState<Record<string, string>>({});
  const [loadingPlan, setLoadingPlan] = useState(isEditMode);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [editActive, setEditActive] = useState(true);
  const [originalEditItem, setOriginalEditItem] =
    useState<ConceptComboDraftItem | null>(null);
  const [baselineFingerprint, setBaselineFingerprint] = useState<string | null>(
    () => (isEditMode ? null : ""),
  );

  const hydrateFromPlan = useCallback((plan: TaggingPlanRow) => {
    const draft = createDraftItemFromPlan(plan);
    setDraftItems([draft]);
    setImpactedByItemId({
      [draft.id]: {
        conceptCode: draft.conceptCode,
        conceptValues: draft.allValues ? [] : draft.conceptValues,
        allValues: draft.allValues ?? false,
      },
    });
    setNameByItemId({ [draft.id]: plan.name?.trim() || "" });
    setEditActive(plan.enabled);
    setOriginalEditItem({
      id: plan.id,
      objectType: plan.objectType,
      objectSubType: plan.objectSubType,
      conceptCode: plan.conceptCode,
    });
    setBaselineFingerprint(null);
    setFieldErrorsByItemId({});
    setLoadError(null);
  }, []);

  useEffect(() => {
    if (!isEditMode || !planId) return;

    const statePlan = (location.state as { plan?: TaggingPlanRow } | null)?.plan;
    if (statePlan && statePlan.id === planId) {
      hydrateFromPlan(statePlan);
      setLoadingPlan(false);
      return;
    }

    let cancelled = false;
    setLoadingPlan(true);
    setLoadError(null);
    fetchAutoTaggingPlanById(planId)
      .then((record) => {
        if (cancelled) return;
        if (!record) {
          setLoadError("Classification plan not found.");
          return;
        }
        hydrateFromPlan(mapTaggingPlanRow(record));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoadError(
          error instanceof Error
            ? error.message
            : "Failed to load Classification plan.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingPlan(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hydrateFromPlan, isEditMode, location.state, planId]);

  const handlePlanLimitReached = useCallback(
    (
      items: ConceptPlanLimitItem[],
      itemsSnapshot?: readonly DraftItem[],
      toastMessage?: string,
      clearOptions?: {
        onlyItemId?: string;
        availableByComboKey?: Readonly<Record<string, number>>;
      },
    ) => {
      if (items.length === 0) return;

      showMessage(toastMessage ?? buildPlanLimitToastMessage(items), "error");

      const sourceItems = itemsSnapshot ?? draftItems;
      const clearIds = new Set(
        collectPlanLimitClearItemIds(
          sourceItems,
          items,
          defaultObjectType,
          clearOptions,
        ),
      );
      if (clearIds.size === 0) return;

      setDraftItems(
        sourceItems.map((item) =>
          clearIds.has(item.id)
            ? {
                ...item,
                conceptCode: "",
                conceptValues: [],
                assessmentPrompt: "",
                allValues: false,
              }
            : item,
        ),
      );

      setFieldErrorsByItemId((prev) => {
        const next = { ...prev };
        for (const id of clearIds) {
          next[id] = {
            ...next[id],
            conceptCode: PLAN_LIMIT_CONCEPT_ERROR,
          };
        }
        return next;
      });
    },
    [defaultObjectType, draftItems, showMessage],
  );

  const { statsByComboKey, assertAvailability } = useDraftConceptStats(
    draftItems,
    defaultObjectType,
    showMessage,
    handlePlanLimitReached as (
      items: ConceptPlanLimitItem[],
      itemsSnapshot?: readonly ConceptComboDraftItem[],
      toastMessage?: string,
      clearOptions?: {
        onlyItemId?: string;
        availableByComboKey?: Readonly<Record<string, number>>;
      },
    ) => void,
  );

  const primaryItem = draftItems[0] ?? null;

  // Capture baseline once after edit hydrate so Cancel can prompt on dirty.
  useEffect(() => {
    if (!isEditMode) return;
    if (loadingPlan || loadError) return;
    if (baselineFingerprint !== null) return;
    if (!primaryItem) return;
    setBaselineFingerprint(
      JSON.stringify({
        objectType: primaryItem.objectType,
        objectSubType: primaryItem.objectSubType,
        conceptCode: primaryItem.conceptCode,
        conceptValues: primaryItem.conceptValues.map((v) => v.code).sort(),
        allValues: primaryItem.allValues ?? false,
        assessmentPrompt: primaryItem.assessmentPrompt,
        assessmentName: nameByItemId[primaryItem.id] ?? "",
        active: editActive,
        taggingApproach: primaryItem.taggingApproach,
        execution: primaryItem.execution,
        layer: primaryItem.layer,
        assessmentTemplate: primaryItem.assessmentTemplate,
        updateAssessmentRecordConcept:
          primaryItem.updateAssessmentRecordConcept,
        updateAssessmentRecords: primaryItem.updateAssessmentRecords,
      }),
    );
  }, [
    baselineFingerprint,
    editActive,
    isEditMode,
    loadError,
    loadingPlan,
    nameByItemId,
    primaryItem,
  ]);

  const isDirty = useMemo(() => {
    if (!isEditMode || !primaryItem || baselineFingerprint === null) return false;
    const initial = JSON.parse(baselineFingerprint) as {
      objectType: string;
      objectSubType: string;
      conceptCode: string;
      conceptValues: string[];
      assessmentPrompt: string;
      allValues: boolean;
      assessmentName: string;
      active: boolean;
      taggingApproach: string;
      execution: string;
      layer: string;
      assessmentTemplate: string;
      updateAssessmentRecordConcept: string;
      updateAssessmentRecords: string[];
    };
    return isEditFormDirty(
      {
        objectType: primaryItem.objectType,
        objectSubType: primaryItem.objectSubType,
        conceptCode: primaryItem.conceptCode,
        conceptValues: primaryItem.conceptValues,
        assessmentPrompt: primaryItem.assessmentPrompt,
        allValues: primaryItem.allValues ?? false,
        assessmentName: nameByItemId[primaryItem.id] ?? "",
        active: editActive,
        taggingApproach: primaryItem.taggingApproach,
        execution: primaryItem.execution,
        layer: primaryItem.layer,
        assessmentTemplate: primaryItem.assessmentTemplate,
        updateAssessmentRecordConcept:
          primaryItem.updateAssessmentRecordConcept,
        updateAssessmentRecords: primaryItem.updateAssessmentRecords,
      },
      {
        ...initial,
        conceptValues: initial.conceptValues.map((code) => ({
          id: code,
          code,
          name: code,
        })),
      },
    );
  }, [
    baselineFingerprint,
    editActive,
    isEditMode,
    nameByItemId,
    primaryItem,
  ]);

  const outlinedPrimarySx = {
    textTransform: "none",
    fontWeight: 500,
    borderColor: primaryColor,
    color: primaryColor,
    "&:hover": {
      borderColor: primaryColor,
      backgroundColor: "rgba(37, 99, 235, 0.04)",
    },
    "& .MuiButton-startIcon .MuiSvgIcon-root": {
      color: primaryColor,
    },
  } as const;

  const handleUploadClick = useCallback(() => {
    setExtractDialogOpen(true);
  }, []);

  const handleCloseExtractDialog = useCallback(() => {
    if (importing) return;
    setExtractDialogOpen(false);
  }, [importing]);

  const handleConfirmExtractFile = useCallback(
    async (file: File) => {
      setImporting(true);
      try {
        const importedItems = await importAutoTaggingPlansFromFile(file);
        const importValidationMessage = validateImportedFormItems(
          importedItems,
          {
            requiresObjectSubType: (objectType) =>
              getSubTypesForObjectType(objectType).length > 0,
          },
        );
        if (importValidationMessage) {
          throw new Error(importValidationMessage);
        }
        const nextDraftItems = importedItems.map(createDraftItemFromImport);
        await assertAvailability(nextDraftItems);
        const names = applyImportedNames(importedItems, nextDraftItems);
        setFieldErrorsByItemId({});
        setDraftItems(nextDraftItems);
        setNameByItemId(names);
        setImpactedByItemId({});
        setExtractDialogOpen(false);
        showMessage(
          `${importedItems.length} item(s) imported from file.`,
          "success",
        );
      } catch (error) {
        if (isConceptPlanLimitError(error)) {
          showMessage(error.message, "error");
        } else {
          showMessage(
            error instanceof Error
              ? error.message
              : "Failed to import assessment plan items.",
            "error",
          );
        }
      } finally {
        setImporting(false);
      }
    },
    [assertAvailability, getSubTypesForObjectType, showMessage],
  );

  const handleAddOneItem = useCallback(() => {
    setDraftItems((prev) => [...prev, createEmptyDraftItem()]);
  }, []);

  const handleRemoveItem = useCallback((id: string) => {
    setDraftItems((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((item) => item.id !== id);
    });
    setNameByItemId((prev) => {
      if (!(id in prev)) return prev;
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
    setImpactedByItemId((prev) => {
      if (!(id in prev)) return prev;
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
    setFieldErrorsByItemId((prev) => {
      if (!(id in prev)) return prev;
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearFieldErrors = useCallback(
    (id: string, fields: DraftItemFieldKey[]) => {
      setFieldErrorsByItemId((prev) => {
        const itemErrors = prev[id];
        if (!itemErrors) return prev;

        const nextItemErrors = { ...itemErrors };
        fields.forEach((field) => {
          delete nextItemErrors[field];
        });

        if (Object.keys(nextItemErrors).length === 0) {
          const next = { ...prev };
          delete next[id];
          return next;
        }

        return { ...prev, [id]: nextItemErrors };
      });
    },
    [],
  );

  const handleUpdateItem = useCallback(
    (
      id: string,
      patch: Partial<
        Pick<
          DraftItem,
          | "objectType"
          | "objectSubType"
          | "objectSubTypeName"
          | "referenceConceptCode"
          | "referenceConceptDisplayName"
          | "referenceDimensions"
          | "conceptCode"
          | "conceptValues"
          | "assessmentPrompt"
          | "allValues"
          | "taggingApproach"
          | "execution"
          | "layer"
          | "assessmentTemplate"
          | "updateAssessmentRecordConcept"
          | "updateAssessmentRecords"
        >
      >,
    ) => {
      const clearedFields = Object.keys(patch) as DraftItemFieldKey[];
      if (patch.conceptCode !== undefined) {
        clearedFields.push("conceptValues", "assessmentPrompt");
      }
      if (patch.conceptValues !== undefined || patch.allValues !== undefined) {
        clearedFields.push("assessmentPrompt");
      }
      if (patch.objectType !== undefined || patch.objectSubType !== undefined) {
        clearedFields.push("conceptCode", "conceptValues", "assessmentPrompt");
      }
      if (patch.assessmentTemplate !== undefined) {
        clearedFields.push("assessmentPrompt");
      }
      clearFieldErrors(id, clearedFields);

      // Functional update avoids stale draftItems overwriting a prior type change
      // (e.g. Insight → RTQ racing with concept-value name hydration).
      setDraftItems((prev) => {
        const nextItems = prev.map((item) => {
          if (item.id !== id) return item;

          const next: DraftItem = { ...item, ...patch };
          // Always clone arrays so picker drafts never share refs across rows.
          if (patch.conceptValues !== undefined) {
            next.conceptValues = [...patch.conceptValues];
          }
          if (patch.updateAssessmentRecords !== undefined) {
            next.updateAssessmentRecords = [...patch.updateAssessmentRecords];
          }
          if (
            patch.objectType !== undefined ||
            patch.objectSubType !== undefined
          ) {
            // Changing type/sub-type resets dependent concept + prompt fields.
            if (patch.conceptCode === undefined) {
              next.conceptCode = "";
              next.conceptValues = [];
              next.allValues = false;
            }
            if (patch.assessmentPrompt === undefined) {
              next.assessmentPrompt = "";
            }
          } else if (
            patch.assessmentPrompt === undefined &&
            (patch.conceptCode !== undefined ||
              patch.conceptValues !== undefined ||
              patch.allValues !== undefined ||
              patch.assessmentTemplate !== undefined)
          ) {
            // Dimension / value / Assessment Template change invalidates prompt
            // so the field can show the matching default-prompt suggestion.
            next.assessmentPrompt = "";
          }
          return next;
        });

        if (isConceptComboSelectionPatch(patch)) {
          const exceeded = collectExceededConceptLimits(
            nextItems,
            statsByComboKey,
            defaultObjectType,
          );
          const allStatsReady = areAllComboStatsLoaded(
            nextItems,
            statsByComboKey,
            defaultObjectType,
          );
          if (allStatsReady && exceeded.length > 0) {
            // Defer so we don't nest another setDraftItems inside this updater.
            queueMicrotask(() => {
              handlePlanLimitReached(exceeded, nextItems, undefined, {
                onlyItemId: id,
              });
            });
            return prev;
          }
        }

        return nextItems;
      });
    },
    [
      clearFieldErrors,
      defaultObjectType,
      handlePlanLimitReached,
      statsByComboKey,
    ],
  );

  const handleCancel = useCallback(() => {
    if (isEditMode && isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    navigate(AI_PLAN_PATH);
  }, [isDirty, isEditMode, navigate]);

  const handleConfirmDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    navigate(AI_PLAN_PATH);
  }, [navigate]);

  const handleSubmit = useCallback(async () => {
    // Ref guard blocks double-submit before React re-renders `submitting`.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    const nextFieldErrors: Record<string, DraftItemFieldErrors> = {};
    const validItems: DraftItem[] = [];

    try {
      for (const item of draftItems) {
        const assessmentPrompt = item.assessmentPrompt.trim();
        const itemErrors = validateDraftItem(
          {
            ...item,
            assessmentName: nameByItemId[item.id] ?? "",
            assessmentPrompt,
            allValues: item.allValues ?? false,
            taggingApproach: item.taggingApproach,
            execution: item.execution,
            layer: item.layer,
            assessmentTemplate: item.assessmentTemplate,
          },
          {
            // Concept V3: Reference Object alone is enough; Doc Type / Value optional.
            requireObjectSubType: false,
          },
        );
        if (hasDraftItemFieldErrors(itemErrors)) {
          nextFieldErrors[item.id] = itemErrors;
        } else {
          validItems.push({ ...item, assessmentPrompt });
        }
      }

      if (validItems.length === 0 || Object.keys(nextFieldErrors).length > 0) {
        setFieldErrorsByItemId(nextFieldErrors);
        return;
      }

      setFieldErrorsByItemId({});

      if (isEditMode) {
        const item = validItems[0];
        if (!item || !originalEditItem) {
          throw new Error("Classification plan not found.");
        }
        await assertEditComboAvailability(
          {
            id: item.id,
            objectType: item.objectType,
            objectSubType: item.objectSubType,
            conceptCode: item.conceptCode,
          },
          originalEditItem,
          defaultObjectType,
        );
        const payload = buildAutoTaggingPlanSubmitItem({
          objectType: item.objectType,
          objectSubType: item.objectSubType,
          conceptCode: item.conceptCode,
          conceptValues: item.allValues ? [] : item.conceptValues,
          assessmentPrompt: item.assessmentPrompt,
          assessmentName: nameByItemId[item.id] ?? "",
          allValues: item.allValues ?? false,
          active: editActive,
          ...buildClassificationSubmitFields(item),
        });
        await updateAutoTaggingPlan(planId, payload);
        showMessage("Item saved successfully.", "success");
      } else {
        await assertAvailability(validItems);
        await submitAssessmentPlanItems(
          validItems.map((item) => ({
            objectType: item.objectType,
            objectSubType: item.objectSubType,
            conceptCode: item.conceptCode,
            conceptValues: item.allValues ? [] : item.conceptValues,
            allValues: item.allValues ?? false,
            assessmentPrompt: item.assessmentPrompt,
            assessmentName: nameByItemId[item.id] ?? "",
            ...buildClassificationSubmitFields(item),
          })),
        );
        showMessage(
          `${validItems.length} item(s) submitted to the global plan.`,
          "success",
        );
      }
      navigate(AI_PLAN_PATH);
    } catch (error) {
      if (isConceptPlanLimitError(error)) {
        handlePlanLimitReached(error.items, draftItems, error.message);
      } else {
        showMessage(
          error instanceof Error
            ? error.message
            : isEditMode
              ? "Failed to update Classification."
              : "Failed to submit assessment plan items.",
          "error",
        );
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [
    assertAvailability,
    defaultObjectType,
    draftItems,
    editActive,
    getSubTypesForObjectType,
    handlePlanLimitReached,
    isEditMode,
    nameByItemId,
    navigate,
    originalEditItem,
    planId,
    showMessage,
  ]);

  if (privilegesLoaded && !canManageAutoTagging) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          px: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Access Denied
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You do not have permission to manage Classification items.
        </Typography>
        <Button onClick={() => navigate(AI_PLAN_PATH)} sx={{ mt: 1 }}>
          Back to Classification AI Plan
        </Button>
      </Box>
    );
  }

  if (isEditMode && loadingPlan) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (isEditMode && loadError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          px: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Unable to load plan
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {loadError}
        </Typography>
        <Button onClick={() => navigate(AI_PLAN_PATH)} sx={{ mt: 1 }}>
          Back to Classification AI Plan
        </Button>
      </Box>
    );
  }

  const pageTitle = isEditMode
    ? "Edit Classification Plan Item"
    : "Add New Classification Plan";
  const pageSubtitle = isEditMode
    ? "Update assessment dimensions and prompt for this Classification plan item."
    : "Configure object types and assessment dimensions to define how two objects are evaluated for relevance.";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          pt: 0,
          px: 0,
          pb: "16px",
          flexShrink: 0,
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
          }}
        >
          <IconButton
            aria-label="Go back"
            onClick={handleCancel}
            sx={{
              width: 32,
              height: 32,
              p: 1,
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            <Box
              component="img"
              src={arrowLeftIcon}
              alt=""
              sx={{
                width: 16.25,
                height: 16,
                display: "block",
                objectFit: "contain",
              }}
            />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Breadcrumbs
              separator="›"
              sx={{
                mb: "4px",
                "& .MuiBreadcrumbs-separator": { color: "#9ca3af", mx: 1 },
              }}
            >
              <Link
                component="button"
                variant="body1"
                onClick={handleCancel}
                sx={{
                  color: primaryColor,
                  textDecoration: "none",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Classification Plan
              </Link>
              <Typography
                sx={{
                  color: "#6B7280",
                  fontSize: "0.875rem",
                  fontWeight: 400,
                }}
              >
                {pageTitle}
              </Typography>
            </Breadcrumbs>
            <Typography sx={{ ...pageSectionTitleSx, mb: "4px" }}>
              {pageTitle}
            </Typography>
            <Typography sx={{ ...pageSectionSubtitleSx, color: "#6B7280" }}>
              {pageSubtitle}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            bgcolor: "#F5F7FA",
            px: { xs: "20px", md: "32px" },
            pt: "20px",
            pb: "24px",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {!isEditMode ? (
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "4px",
                  border: "1px solid #E5E7EB",
                  p: { xs: "16px", md: "20px" },
                  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                }}
              >
                <Typography sx={sectionLabelSx}>Extract From File</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 18 }} />}
                  onClick={handleUploadClick}
                  disabled={submitting}
                  sx={{
                    ...outlinedPrimarySx,
                    height: 36,
                    minHeight: 36,
                    px: 1.75,
                    borderRadius: "6px",
                    fontWeight: 500,
                  }}
                >
                  Upload File
                </Button>
              </Box>
            ) : null}

            {draftItems.map((item) => {
              const fieldErrors = fieldErrorsByItemId[item.id];
              const referenceError =
                fieldErrors?.objectType || fieldErrors?.objectSubType;
              const impactedError =
                fieldErrors?.conceptCode || fieldErrors?.conceptValues;
              const assessmentName = nameByItemId[item.id] ?? "";
              const impacted = impactedByItemId[item.id] ?? {
                conceptCode: item.conceptCode,
                conceptValues: item.allValues ? [] : item.conceptValues,
                allValues: item.allValues ?? false,
              };
              const showPromptField = shouldShowPrompt({
                objectType: item.objectType,
                conceptCode: item.conceptCode,
                allValues: item.allValues,
                conceptValues: item.conceptValues,
              });
              const canRemove = !isEditMode && draftItems.length > 1;
              return (
                <Box
                  key={item.id}
                  sx={{
                    position: "relative",
                    bgcolor: "#fff",
                    borderRadius: "4px",
                    border: "1px solid #E5E7EB",
                    p: { xs: "16px", md: "20px" },
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                  }}
                >
                  {canRemove ? (
                    <IconButton
                      aria-label="Remove item"
                      title="Remove item"
                      onClick={() => handleRemoveItem(item.id)}
                      disabled={submitting}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "#99A1AF",
                        "&:hover": { color: "#E7000B", bgcolor: "transparent" },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  ) : null}

                  <AssessmentNameField
                    key={`${item.id}-assessment-name`}
                    value={assessmentName}
                    options={assessmentNameOptions}
                    loading={assessmentNamesLoading}
                    disabled={submitting}
                    error={Boolean(fieldErrors?.assessmentName)}
                    helperText={fieldErrors?.assessmentName}
                    onOpen={() => {
                      setAssessmentNameMenuActive(true);
                      setAssessmentNameReloadToken((token) => token + 1);
                    }}
                    onChange={(nextValue) => {
                      setNameByItemId((prev) => ({
                        ...prev,
                        [item.id]: nextValue,
                      }));
                      if (fieldErrors?.assessmentName && nextValue.trim()) {
                        setFieldErrorsByItemId((prev) => {
                          const current = prev[item.id];
                          if (!current?.assessmentName) return prev;
                          const { assessmentName: _removed, ...rest } =
                            current;
                          if (Object.keys(rest).length === 0) {
                            const { [item.id]: _item, ...others } = prev;
                            return others;
                          }
                          return { ...prev, [item.id]: rest };
                        });
                      }
                    }}
                  />

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "minmax(0,1fr) 44px minmax(0,1fr)",
                      },
                      gap: { xs: 1.5, md: "28px" },
                      // Top-align so Impacted helper/error text does not sink
                      // Reference / push Tagging Approach row into a staggered layout.
                      alignItems: "start",
                      mt: 1.75,
                    }}
                  >
                    <Box sx={{ minWidth: 0, position: "relative" }}>
                      <ReferenceItemsCascadePicker
                        value={{
                          objectType: item.objectType,
                          objectSubType: item.objectSubType,
                          objectSubTypeName: item.objectSubTypeName,
                          referenceConceptCode: item.referenceConceptCode,
                          referenceConceptDisplayName:
                            item.referenceConceptDisplayName,
                          referenceDimensions: item.referenceDimensions,
                          conceptCode: item.conceptCode,
                          conceptValues: item.conceptValues,
                          allValues: item.allValues ?? false,
                        }}
                        error={Boolean(referenceError)}
                        helperText={referenceError}
                        disabled={submitting}
                        onChange={(next) => {
                          const objectChanged =
                            next.objectType !== item.objectType ||
                            next.objectSubType !== item.objectSubType ||
                            (next.referenceConceptCode ?? "") !==
                              (item.referenceConceptCode ?? "") ||
                            JSON.stringify(next.referenceDimensions ?? []) !==
                              JSON.stringify(item.referenceDimensions ?? []);
                          if (objectChanged) {
                            const clearTagApproach =
                              !shouldShowTaggingApproach(next.objectType);
                            const objectTypeChanged =
                              next.objectType !== item.objectType;
                            if (objectTypeChanged) {
                              setImpactedByItemId((prev) => {
                                const { [item.id]: _removed, ...rest } = prev;
                                return rest;
                              });
                            }
                            handleUpdateItem(item.id, {
                              objectType: next.objectType,
                              objectSubType: next.objectSubType,
                              objectSubTypeName: next.objectSubTypeName,
                              referenceConceptCode: next.referenceConceptCode,
                              referenceConceptDisplayName:
                                next.referenceConceptDisplayName,
                              referenceDimensions: next.referenceDimensions,
                              // Reference drives assessment_record concepts — reset selection.
                              updateAssessmentRecordConcept: "",
                              updateAssessmentRecords: [],
                              ...(objectTypeChanged
                                ? {
                                    conceptCode: "",
                                    conceptValues: [],
                                    assessmentPrompt: "",
                                    allValues: false,
                                    ...(clearTagApproach
                                      ? { taggingApproach: "" }
                                      : {}),
                                  }
                                : {}),
                            });
                            return;
                          }
                          handleUpdateItem(item.id, {
                            objectType: next.objectType,
                            objectSubType: next.objectSubType,
                            objectSubTypeName: next.objectSubTypeName,
                            referenceConceptCode: next.referenceConceptCode,
                            referenceConceptDisplayName:
                              next.referenceConceptDisplayName,
                            referenceDimensions: next.referenceDimensions,
                            conceptCode: item.conceptCode,
                            conceptValues: item.conceptValues,
                            assessmentPrompt: item.assessmentPrompt,
                            allValues: item.allValues ?? false,
                          });
                        }}
                      />
                    </Box>
                    <ClassificationPlanFlowArrow />
                    <Box sx={{ minWidth: 0, position: "relative" }}>
                      <ImpactedItemsCascadePicker
                        value={impacted}
                        objectType={item.objectType}
                        objectSubType={item.objectSubType}
                        error={Boolean(impactedError)}
                        helperText={impactedError}
                        disabled={submitting}
                        onChange={(next) => {
                          setImpactedByItemId((prev) => ({
                            ...prev,
                            [item.id]: next,
                          }));
                          handleUpdateItem(item.id, {
                            conceptCode: next.conceptCode,
                            conceptValues: next.allValues
                              ? []
                              : next.conceptValues,
                            assessmentPrompt: "",
                            allValues: next.allValues,
                          });
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      borderTop: "1px solid #F0F1F3",
                      mt: 2.75,
                      mb: 2.25,
                    }}
                  />

                  <ClassificationPlanConfigFields
                    key={`${item.id}-config-fields`}
                    value={{
                      taggingApproach: item.taggingApproach,
                      execution: item.execution,
                      layer: item.layer,
                      assessmentTemplate: item.assessmentTemplate,
                      updateAssessmentRecordConcept:
                        item.updateAssessmentRecordConcept,
                      updateAssessmentRecords: item.updateAssessmentRecords,
                    }}
                    objectType={item.objectType}
                    disabled={submitting}
                    errors={{
                      taggingApproach: fieldErrors?.taggingApproach,
                      execution: fieldErrors?.execution,
                      layer: fieldErrors?.layer,
                      assessmentTemplate: fieldErrors?.assessmentTemplate,
                    }}
                    onChange={(patch) => {
                      handleUpdateItem(item.id, patch);
                      setFieldErrorsByItemId((prev) => {
                        const current = prev[item.id];
                        if (!current) return prev;
                        const next = { ...current };
                        if (patch.taggingApproach !== undefined) {
                          delete next.taggingApproach;
                        }
                        if (patch.execution !== undefined) {
                          delete next.execution;
                        }
                        if (patch.layer !== undefined) {
                          delete next.layer;
                        }
                        if (patch.assessmentTemplate !== undefined) {
                          delete next.assessmentTemplate;
                          delete next.assessmentPrompt;
                        }
                        if (Object.keys(next).length === 0) {
                          const { [item.id]: _item, ...others } = prev;
                          return others;
                        }
                        return { ...prev, [item.id]: next };
                      });
                    }}
                  />

                  {showPromptField ? (
                    <DefaultAssessmentPromptPrefetch
                      conceptCode={item.conceptCode}
                      objectType={item.objectType}
                      assessmentTemplate={item.assessmentTemplate}
                    />
                  ) : null}

                  {showPromptField ? (
                    <Box sx={{ mt: 1.75 }}>
                      <AssessmentPromptFieldWithDefault
                        conceptCode={item.conceptCode}
                        objectType={item.objectType}
                        assessmentTemplate={item.assessmentTemplate}
                        value={item.assessmentPrompt}
                        error={Boolean(fieldErrors?.assessmentPrompt)}
                        helperText={fieldErrors?.assessmentPrompt}
                        maxLength={CLASSIFICATION_PROMPT_MAX_LENGTH}
                        label="Prompt"
                        required={false}
                        defaultPromptInfoText={ASSESSMENT_PROMPT_DEFAULT_INFO}
                        onChange={(nextValue) =>
                          handleUpdateItem(item.id, {
                            assessmentPrompt: nextValue,
                          })
                        }
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}

            {!isEditMode ? (
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "4px",
                  border: "1px solid #E5E7EB",
                  px: 2.5,
                  py: 1.25,
                  display: "flex",
                  justifyContent: "center",
                  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={handleAddOneItem}
                  disabled={submitting}
                  sx={{
                    ...outlinedPrimarySx,
                    width: 520,
                    maxWidth: "100%",
                    height: 30,
                    minHeight: 30,
                    borderRadius: "3px",
                    fontWeight: 400,
                    fontSize: "0.8125rem",
                  }}
                >
                  Add One Item
                </Button>
              </Box>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            flexShrink: 0,
            bgcolor: "#fff",
            px: { xs: "20px", md: "32px" },
            py: "16px",
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={submitting}
            sx={outlinedPrimarySx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{
              ...brandContainedButtonSx,
              bgcolor: `${primaryColor} !important`,
              color: `${primaryFontColor} !important`,
              minWidth: 88,
              "&:hover": {
                bgcolor: `${primaryColor} !important`,
              },
            }}
          >
            {submitting
              ? isEditMode
                ? "Saving…"
                : "Submitting…"
              : isEditMode
                ? "Save"
                : "Submit"}
          </Button>
        </Box>
      </Box>

      <ExtractFromFileDialog
        open={extractDialogOpen}
        confirming={importing}
        onClose={handleCloseExtractDialog}
        onConfirm={handleConfirmExtractFile}
      />

      <DiscardEnhancedTaggingRuleChangesDialog
        open={discardConfirmOpen}
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
      />
    </Box>
  );
}
